import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Modal, Tabs, Wizard, StatusBadge, EmptyState, ConfirmDialog } from '@/components/ui';
import type { WizardStep } from '@/components/ui/Wizard';
import { FormField } from '@/components/ui/FormField';
import { TextInput } from '@/components/ui/TextInput';
import { Icon } from '@/components/ui/Icon';
import { labelFor } from '@/shared/lib/labels';
import { adminApi, type DialerDefaults, type DialerSettings, type CampaignOverviewCard, type CampaignLead, type LeadOwnership, type AssignLeadsBody } from '@/shared/api/adminApi';
import { agentApi } from '@/shared/api/agentApi';
import { useSipStore } from '@/personas/agent/stores/sipStore';
import { toast } from '@/components/ui/Toast';
import { LeadImportInput, type ParsedLead } from './leads/LeadImportInput';
import { useResource } from '@/shared/hooks/useResource';
import { downloadCsv } from '@/shared/lib/downloadCsv';
import { DialerSettingsForm } from './campaign/DialerSettingsForm';
import { DIAL_STRATEGIES } from './campaign/dialerSettings';
import { CampaignDidBank } from './campaign/CampaignDidBank';
import { CampaignDispositions } from './campaign/CampaignDispositions';
import { CampaignSetupWizard } from './campaign/CampaignSetupWizard';
import { buildSettingsPayload } from './campaign/dialerSettings';
import { CampaignCard } from './campaign/CampaignCard';

interface Campaign {
  id: number;
  name: string;
  type: string;
  status: string;
  dialer_mode: string;
  settings?: Record<string, unknown> | null;
}

const STATUS: Record<string, { label: string; tone: 'ok' | 'warn' | 'danger' | 'neutral' }> = {
  draft: { label: 'טיוטה', tone: 'neutral' },
  active: { label: 'פעיל', tone: 'ok' },
  paused: { label: 'מושהה', tone: 'warn' },
  completed: { label: 'הושלם', tone: 'neutral' },
};
const TYPE_LABEL: Record<string, string> = { outbound: 'חיוג יוצא', callback: 'חזרה', mixed: 'מעורב' };
const MODE_LABEL: Record<string, string> = { preview: 'Preview', progressive: 'Progressive', predictive: 'Predictive' };
const MODE_EXPLAIN: Record<string, string> = {
  preview: 'הנציג רואה את פרטי הלקוח לפני השיחה ומחליט בעצמו מתי לחייג. הקצב האיטי ביותר, אבל מקסימום הכנה — מתאים לשיחות מורכבות או לידים יקרים.',
  progressive: 'המערכת מחייגת אוטומטית ללקוח הבא ברגע שנציג מתפנה — שיחה אחת לכל נציג. קצב אחיד בלי זמן מת, והלקוח תמיד עונה לנציג זמין.',
  predictive: 'המערכת מחייגת לכמה לקוחות במקביל לכל נציג פנוי ומנבאת מתי יתפנה הנציג הבא. הקצב המהיר ביותר — מתאים לרשימות גדולות, אך שיחה עלולה להינטש אם אין נציג פנוי (רף הנטישה נשלט בהגדרות).',
};
const DAY_NAMES = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
interface BizHour { day_of_week: number; open?: string; close?: string; enabled: boolean }

export function Campaigns() {
  // אשף ההקמה החדש (ברירת מחדל) מול הטופס הישן (11 שלבים, "הקמה
  // מתקדמת"). שניהם חיים זה לצד זה זמנית: האשף החדש שולח לידים/נציגים/
  // מספרים ולכן מוליד קמפיין שיכול לחייג, בעוד שהישן נשאר כרשת ביטחון
  // עד שנדע שהחדש מחזיק בשטח.
  const [setupOpen, setSetupOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [drawer, setDrawer] = useState<Campaign | null>(null);
  const [tab, setTab] = useState<'overview' | 'mode' | 'settings' | 'agents' | 'leads' | 'stats'>('overview');
  const [deleteTarget, setDeleteTarget] = useState<Campaign | null>(null);
  // Tenant-wide dialer defaults — the whole settings object a campaign inherits
  // when it doesn't override a given key.
  const [defaults, setDefaults] = useState<DialerDefaults | null>(null);
  const [defaultsOpen, setDefaultsOpen] = useState(false);
  // סינון הכרטיסים לפי סטטוס (בעיצוב: בורר מחלקות — אין מחלקות בנתוני הקמפיין).
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'paused' | 'completed'>('all');
  // ייבוא לידים מהסרגל העליון — קודם בוחרים קמפיין יעד.
  const [importOpen, setImportOpen] = useState(false);

  // Detailed overview cards, polled so the live numbers stay fresh.
  const { data: cards, loading, reload: refetch } = useResource(() => adminApi.campaignsOverview(), { pollMs: 15000 });

  // קמפיינים שהמנהל עצמו מצורף אליהם — כדי לחייג ישירות מהכרטיס.
  const [joinedIds, setJoinedIds] = useState<Set<number>>(new Set());
  const navigate = useNavigate();
  const [dialingId, setDialingId] = useState<number | null>(null);
  const [resetBusyId, setResetBusyId] = useState<number | null>(null);
  const [restartTarget, setRestartTarget] = useState<CampaignOverviewCard | null>(null);
  const [windowConfirm, setWindowConfirm] = useState<{ card: CampaignOverviewCard; message: string } | null>(null);
  useEffect(() => {
    void agentApi.dialerCampaigns()
      .then((rows) => setJoinedIds(new Set((rows as { id: number }[]).map((r) => r.id))))
      .catch(() => {});
  }, []);

  async function joinAndDial(card: CampaignOverviewCard, forceWindow = false) {
    setDialingId(card.id);
    try {
      if (!joinedIds.has(card.id)) {
        const res = await adminApi.joinCampaign(card.id);
        setJoinedIds((s) => new Set(s).add(card.id));
        toast(`הצטרפת לקמפיין — שלוחה ${res.extension}`, 'ok');
      }
      // הלקוח שיענה מגושר לסופטפון — בלי קו רשום הוא שומע "כל הנציגים
      // תפוסים" ומנותק, והמסך מציג "ענה" בלי שיחה ובלי "נתק". ההצטרפות
      // יוצרת שלוחה חדשה שהדפדפן עוד לא רשום עליה (28/09/2026, קמפיין 7:
      // שלוחה נוצרה 12:52:22, הסבב יצא 12:52:23, הלקוח ענה ונזרק).
      if (!(await useSipStore.getState().refreshLine())) {
        toast('הסופטפון לא מחובר — לא חייגנו. בדוק את החיבור או רענן את הדף, ואז חייג שוב.', 'danger');
        navigate(`/admin/campaign?campaign=${card.id}`);
        return;
      }
      const batch = await agentApi.dialerDialBatch(card.id, forceWindow ? { forceWindow: true } : undefined);
      // מחוץ לשעות: הודעה בלבד — השרת כבר חייג.
      if (batch?.window_notice) toast(batch.window_notice, 'warn');
      toast(`מחייג ${batch?.dialed ?? 0} קווים — כשלקוח יענה השיחה תצלצל בסופטפון שלך`, 'ok');
      // עוברים למסך העבודה של אותו קמפיין: בלי זה השיחה מצלצלת בסופטפון
      // בזמן שהמסך עדיין מציג רשימת קמפיינים, ואין על מי לדבר.
      // מזהה הסבב עובר הלאה כדי שמסך העבודה יציג את כל מי שחויג עכשיו.
      navigate(`/admin/campaign?campaign=${card.id}${batch?.batch ? `&batch=${batch.batch}` : ''}`);
    } catch (e: unknown) {
      const data = (e as { response?: { data?: { message?: string; code?: string; can_force?: boolean } } })?.response?.data;
      // מחוץ לשעות החיוג ולמשתמש יש הרשאת עקיפה — מציעים חיוג בדיקה מפורש
      // במקום להשאיר אותו תקוע מול toast.
      if (data?.code === 'outside_window' && data.can_force && !forceWindow) {
        setWindowConfirm({ card, message: data.message ?? 'מחוץ לחלון החיוג של הקמפיין.' });
        return;
      }
      // "אין לידים פנויים כרגע" אינו כשל של המערכת אלא מצב תקין של התור,
      // ולכן הוא מוצג כאזהרה — ובכל זאת נכנסים למסך העבודה, שבו הנציג
      // רואה את הסיבה המלאה ואת כפתור החזרת הלידים החסומים.
      const msg = data?.message ?? 'החיוג נכשל';
      const queueEmpty = msg.includes('אין לידים');
      toast(msg, queueEmpty ? 'warn' : 'danger');
      if (queueEmpty) navigate(`/admin/campaign?campaign=${card.id}`);
    } finally {
      setDialingId(null);
    }
  }

  async function leaveCampaign(card: CampaignOverviewCard) {
    try {
      await adminApi.leaveCampaign(card.id);
      setJoinedIds((s) => { const n = new Set(s); n.delete(card.id); return n; });
      toast('עזבת את הקמפיין', 'ok');
    } catch {
      toast('שגיאה בעזיבת הקמפיין', 'danger');
    }
  }

  useEffect(() => {
    void adminApi.dialerDefaults().then((d) => { if (d) setDefaults(d); }).catch(() => {});
  }, []);

  // שעות הפעילות של החברה — מוצגות בצעד "שעות פעילות" של האשף כדי
  // שיהיה ברור על אילו שעות הקמפיין יתבסס.
  const [bizHours, setBizHours] = useState<BizHour[] | null>(null);
  useEffect(() => {
    if (!wizardOpen || bizHours !== null) return;
    void adminApi.hours()
      .then((d) => setBizHours(((d as { hours?: BizHour[] })?.hours) ?? []))
      .catch(() => setBizHours([]));
  }, [wizardOpen, bizHours]);

  const defaultLines = defaults?.lines_per_agent ?? 3;

  // Open the campaign popup on a given tab. Opens instantly with the card's
  // data, then hydrates type/dialer_mode/settings from the real record.
  function openCampaign(card: CampaignOverviewCard, startTab: typeof tab = 'overview') {
    setTab(startTab);
    setDrawer({ id: card.id, name: card.name, type: 'outbound', status: card.status, dialer_mode: 'preview', settings: null });
    void adminApi.campaign(card.id).then((c) => {
      if (!c || typeof c !== 'object' || Array.isArray(c)) return;
      setDrawer((d) => (d && d.id === card.id ? { ...d, ...(c as Partial<Campaign>), id: card.id } : d));
    }).catch(() => {});
  }

  async function toggleActive(card: CampaignOverviewCard, next: boolean) {
    try {
      await adminApi.updateCampaign(card.id, { status: next ? 'active' : 'paused' });
      toast(next ? 'הקמפיין הופעל' : 'הקמפיין הושהה', 'ok');
      void refetch();
    } catch (e) {
      // 🔴 ה-catch הריק הסתיר את הסיבה: השרת מחזיר 422 עם הסבר בעברית
      // ("מעבר לא חוקי בקמפיין: completed → active"), והמסך הציג
      // "שגיאה בעדכון סטטוס" — כך שלמנהל לא הייתה שום דרך לדעת למה.
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast(msg || 'שגיאה בעדכון סטטוס', 'danger');
    }
  }

  async function exportLeads(card: CampaignOverviewCard) {
    try {
      await downloadCsv('/admin/leads/export', { campaign_id: card.id }, `campaign-${card.id}-leads.csv`);
    } catch {
      toast('שגיאה בייצוא לידים', 'danger');
    }
  }

  /** מחזיר לתור לידים שנחסמו על ידי תקרה, ומרענן כדי שהמונה בכרטיס יתעדכן. */
  async function returnCapped(card: CampaignOverviewCard) {
    setResetBusyId(card.id);
    try {
      const r = await adminApi.returnCappedLeads(card.id);
      toast(r.message ?? `${r.returned} לידים הוחזרו לתור`, r.returned > 0 ? 'ok' : 'warn');
      await refetch();
    } catch {
      toast('שגיאה בהחזרת הלידים', 'danger');
    } finally {
      setResetBusyId(null);
    }
  }

  /** הפעלה מחדש — נקראת רק אחרי אישור בדיאלוג, כי היא נוגעת בכל הקמפיין. */
  async function restart(card: CampaignOverviewCard) {
    setResetBusyId(card.id);
    try {
      const r = await adminApi.restartCampaign(card.id);
      toast(r.message ?? `הקמפיין הופעל מחדש — ${r.restarted} לידים`, 'ok');
      await refetch();
    } catch {
      toast('שגיאה בהפעלת הקמפיין מחדש', 'danger');
    } finally {
      setResetBusyId(null);
    }
  }

  async function setStatus(c: Campaign, status: string) {
    try {
      await adminApi.updateCampaign(c.id, { status });
      toast('הסטטוס עודכן', 'ok');
      setDrawer((d) => (d && d.id === c.id ? { ...d, status } : d));
      void refetch();
    } catch {
      toast('שגיאה בעדכון סטטוס', 'danger');
    }
  }

  async function changeMode(c: Campaign, mode: string) {
    try {
      await adminApi.updateCampaign(c.id, { dialer_mode: mode });
      toast('מצב החיוג עודכן', 'ok');
      setDrawer((d) => (d && d.id === c.id ? { ...d, dialer_mode: mode } : d));
      void refetch();
    } catch {
      toast('שגיאה בעדכון מצב החיוג', 'danger');
    }
  }

  async function changeStrategy(c: Campaign, strategy: string) {
    // מיזוג עם ההגדרות הקיימות — שליחת settings חלקי דורסת את השאר.
    const settings = { ...(c.settings ?? {}), dial_strategy: strategy };
    try {
      await adminApi.updateCampaign(c.id, { settings });
      toast('סדר החיוג עודכן', 'ok');
      setDrawer((d) => (d && d.id === c.id ? { ...d, settings } : d));
      void refetch();
    } catch {
      toast('שגיאה בעדכון סדר החיוג', 'danger');
    }
  }

  async function duplicate(c: Campaign) {
    try {
      const copy = await adminApi.duplicateCampaign(c.id);
      toast(`הקמפיין שוכפל — "${copy?.name ?? 'עותק'}" נוצר במצב מושהה`, 'ok');
      setDrawer(null);
      void refetch();
    } catch {
      toast('שגיאה בשכפול הקמפיין', 'danger');
    }
  }

  async function remove(c: Campaign) {
    try {
      await adminApi.deleteCampaign(c.id);
      toast('הקמפיין נמחק', 'ok');
      setDeleteTarget(null);
      setDrawer(null);
      void refetch();
    } catch {
      toast('שגיאה במחיקה', 'danger');
    }
  }

  const steps: WizardStep[] = [
    { id: 'basics', title: 'פרטים בסיסיים', render: (ctx) => (
      <div className="stack">
        <FormField label="שם הקמפיין" required>
          <TextInput value={(ctx.data.name as string) ?? ''} onChange={(e) => ctx.setData({ name: e.target.value })} />
        </FormField>
      </div>
    ), validate: (d) => !!d.name || 'יש למלא שם' },
    { id: 'type', title: 'סוג קמפיין', render: (ctx) => (
      <FormField label="סוג">
        <select
          value={(ctx.data.type as string) ?? 'outbound'}
          onChange={(e) => ctx.setData({ type: e.target.value })}
          style={{ width: '100%', padding: 10, border: '1px solid var(--line)', borderRadius: 'var(--r-2)', background: 'var(--surface)' }}
        >
          <option value="outbound">חיוג יוצא</option>
          <option value="callback">קמפיין חזרה</option>
          <option value="mixed">מעורב</option>
        </select>
      </FormField>
    ) },
    { id: 'mode', title: 'מצב חיוג', render: (ctx) => {
      const mode = (ctx.data.dialer_mode as string) ?? 'preview';
      return (
        <div className="stack">
          <FormField label="מצב">
            <select
              value={mode}
              onChange={(e) => ctx.setData({ dialer_mode: e.target.value })}
              style={{ width: '100%', padding: 10, border: '1px solid var(--line)', borderRadius: 'var(--r-2)', background: 'var(--surface)' }}
            >
              <option value="preview">Preview - הצגה לנציג לפני חיוג</option>
              <option value="progressive">Progressive - חיוג אוטומטי</option>
              <option value="predictive">Predictive - חיוג מנובא</option>
            </select>
          </FormField>
          <div style={{ padding: 12, background: 'var(--surface-2)', borderRadius: 'var(--r-2)', fontSize: 'var(--t-sm)', lineHeight: 1.6 }}>
            <strong>{MODE_LABEL[mode]}:</strong> {MODE_EXPLAIN[mode]}
          </div>
        </div>
      );
    } },
    { id: 'predictive', title: 'הגדרות חייגן חזוי', render: (ctx) => {
      const isPredictive = (ctx.data.dialer_mode as string) === 'predictive';
      const s = (ctx.data.settings as Record<string, unknown>) ?? {};
      const setS = (patch: Record<string, unknown>) => ctx.setData({ settings: { ...s, ...patch } });
      if (!isPredictive) {
        return <p style={{ color: 'var(--muted)' }}>הגדרות אלו רלוונטיות רק למצב Predictive. אפשר לדלג.</p>;
      }
      const pacingMode = (s.pacing_mode as string) ?? 'fixed';
      return (
        <div className="stack">
          <p style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)', margin: 0 }}>
            ניתן לראות כיצד ההגדרות משפיעות ב<strong>סימולטור חייגן חזוי</strong>.
          </p>
          <FormField label="שיטת חיוג">
            <select value={pacingMode} onChange={(e) => setS({ pacing_mode: e.target.value })}
              style={{ width: '100%', padding: 10, border: '1px solid var(--line)', borderRadius: 'var(--r-2)', background: 'var(--surface)' }}>
              <option value="fixed">קבוע — מספר קווים שתקבע לכל נציג</option>
              <option value="adaptive">חכם — האלגוריתם מווסת לפי אחוז המענה</option>
            </select>
          </FormField>
          {pacingMode === 'fixed' && (
            <FormField label="קווים לכל נציג" hint={`כל נציג פנוי מחייג למספר לקוחות בו-זמנית; הראשון שעונה נשאר, השאר חוזרים לתור. ברירת המחדל של העסק: ${defaultLines}`}>
              <TextInput type="number" value={String((s.lines_per_agent as number) ?? defaultLines)} onChange={(e) => setS({ lines_per_agent: Number(e.target.value) })} dir="ltr" />
            </FormField>
          )}
          {pacingMode === 'adaptive' && (
          <FormField label="יחס חיוג מקסימלי (קווים לכל נציג)" hint="תקרת ה-over-dialing. ברירת מחדל 3.0">
            <TextInput type="number" value={String((s.dial_ratio_max as number) ?? 3)} onChange={(e) => setS({ dial_ratio_max: Number(e.target.value) })} dir="ltr" />
          </FormField>
          )}
          <FormField label="רף נטישה מקסימלי (%)" hint="בלם רגולטורי. ברירת מחדל 3% — לא מומלץ לעלות מעל זה">
            <TextInput type="number" value={String(Math.round(((s.abandon_rate_cap as number) ?? 0.03) * 100))} onChange={(e) => setS({ abandon_rate_cap: Number(e.target.value) / 100 })} dir="ltr" />
          </FormField>
          <FormField label="זיהוי משיבון (AMD)">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--t-sm)' }}>
              <input type="checkbox" checked={(s.amd_enabled as boolean) ?? true} onChange={(e) => setS({ amd_enabled: e.target.checked })} />
              נתק אוטומטית משיבון/אין-מענה ותזמן חיוג חוזר
            </label>
          </FormField>
          <FormField label="תזמון חיוג חוזר במשיבון/אין-מענה (דקות)" hint="ברירת מחדל 120">
            <TextInput type="number" value={String((s.reschedule_no_answer_minutes as number) ?? 120)} onChange={(e) => setS({ reschedule_no_answer_minutes: Number(e.target.value) })} dir="ltr" />
          </FormField>
        </div>
      );
    } },
    { id: 'attempts', title: 'ניסיונות', render: (ctx) => (
      <div className="stack">
        <FormField label="מקסימום ניסיונות">
          <TextInput type="number" value={String(ctx.data.max_attempts ?? 3)} onChange={(e) => ctx.setData({ max_attempts: Number(e.target.value) })} dir="ltr" />
        </FormField>
        <FormField label="המתנה בין ניסיונות (דקות)">
          <TextInput type="number" value={String(ctx.data.retry_minutes ?? 60)} onChange={(e) => ctx.setData({ retry_minutes: Number(e.target.value) })} dir="ltr" />
        </FormField>
      </div>
    ) },
    { id: 'schedule', title: 'שעות פעילות', render: () => (
      <div className="stack">
        <p style={{ color: 'var(--muted)', margin: 0 }}>
          הקמפיין יחייג רק בשעות הפעילות של החברה (מוצגות למטה). ניתן לשנות אותן במסך הגדרות ← שעות פעילות, ולהגדיר חריגות פר-קמפיין במסך הקמפיין לאחר היצירה.
        </p>
        {bizHours === null ? (
          <p style={{ color: 'var(--muted)', margin: 0 }}>טוען שעות פעילות…</p>
        ) : bizHours.length === 0 ? (
          <p style={{ color: 'var(--muted)', margin: 0 }}>לא הוגדרו עדיין שעות פעילות לחברה — מומלץ להגדיר אותן במסך ההגדרות.</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
            {bizHours.map((h) => (
              <div key={h.day_of_week} style={{ padding: '8px 12px', background: 'var(--surface-2)', borderRadius: 'var(--r-2)', fontSize: 'var(--t-sm)', opacity: h.enabled ? 1 : 0.55 }}>
                <strong>{DAY_NAMES[h.day_of_week] ?? h.day_of_week}</strong>
                <div dir="ltr" style={{ textAlign: 'end' }}>{h.enabled ? `${h.open ?? '—'}–${h.close ?? '—'}` : 'סגור'}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    ) },
    { id: 'script', title: 'תסריט', render: () => <p style={{ color: 'var(--muted)' }}>ניתן לבחור תסריט קיים או ליצור חדש לאחר יצירת הקמפיין.</p> },
    { id: 'leads', title: 'רשימת לידים', render: () => <p style={{ color: 'var(--muted)' }}>אפשר להעלות לידים אחרי יצירת הקמפיין.</p> },
    { id: 'dispositions', title: 'קודי סיום', render: () => <p style={{ color: 'var(--muted)' }}>השתמש בקודי סיום ברירת מחדל או הוסף קודים מותאמים.</p> },
    { id: 'team', title: 'נציגים', render: () => <p style={{ color: 'var(--muted)' }}>הקצה נציגים לקמפיין במסך הניהול.</p> },
    { id: 'advanced', title: 'הגדרות מתקדמות', render: (ctx) => {
      const s = (ctx.data.settings as Record<string, unknown>) ?? {};
      const dialerKeys: (keyof DialerSettings)[] = ['lines_per_agent', 'dial_strategy', 'wrapup_required', 'wrapup_blocks_next', 'auto_dial_next', 'transfer_allowed', 'max_unanswered_per_day', 'retry_ladder', 'caller_id_strategy', 'rotate_threshold', 'unanswered_outcomes', 'quick_actions'];
      const value: Partial<DialerSettings> = {};
      dialerKeys.forEach((k) => { if (s[k] !== undefined) (value as Record<string, unknown>)[k] = s[k]; });
      const inherited = defaults ? (() => { const { strategies: _s, ...rest } = defaults; return rest as Partial<DialerSettings>; })() : undefined;
      return (
        <div className="stack">
          <p style={{ margin: 0, fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>
            שדות שתשאיר ריקים יורשו מברירת המחדל של העסק. אפשר לשנות זאת גם אחרי היצירה.
          </p>
          <DialerSettingsForm
            value={value}
            inherited={inherited}
            onChange={(next) => ctx.setData({ settings: { ...s, ...next } })}
          />
        </div>
      );
    } },
    { id: 'review', title: 'סקירה', render: (ctx) => (
      <Card>
        <pre style={{ margin: 0, fontFamily: 'var(--font-mono)', fontSize: 12, overflow: 'auto' }}>{JSON.stringify(ctx.data, null, 2)}</pre>
      </Card>
    ) },
  ];

  async function finish(data: Record<string, unknown>) {
    await adminApi.createCampaign(data);
    toast('הקמפיין נוצר', 'ok');
    setWizardOpen(false);
    void refetch();
  }

  const all = cards ?? [];
  const inFilter = (c: CampaignOverviewCard, f: typeof statusFilter) =>
    f === 'all' ? true : f === 'active' ? c.active : f === 'paused' ? !c.active && c.status !== 'completed' : c.status === 'completed';
  const list = all.filter((c) => inFilter(c, statusFilter));
  const filterTabs: { id: typeof statusFilter; label: string }[] = [
    { id: 'all', label: 'הכל' },
    { id: 'active', label: 'פעילים' },
    { id: 'paused', label: 'מושהים' },
    { id: 'completed', label: 'הסתיימו' },
  ];

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      {/* סרגל עליון: סינון מימין, פעולות משמאל (הראשית כחולה בקצה) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {all.length > 0 && (
          <div className="seg" role="tablist" aria-label="סינון קמפיינים" style={{ maxWidth: '100%', overflowX: 'auto' }}>
            {filterTabs.map((t) => {
              const n = all.filter((c) => inFilter(c, t.id)).length;
              return (
                <button key={t.id} type="button" role="tab" aria-selected={statusFilter === t.id} onClick={() => setStatusFilter(t.id)}>
                  {t.label}
                  {t.id !== 'all' && n > 0 && <span className="seg-count">{n}</span>}
                </button>
              );
            })}
          </div>
        )}
        <span style={{ flex: 1 }} />
        <button type="button" className="btn ghost" onClick={() => setDefaultsOpen(true)} disabled={!defaults}>
          <Icon name="settings" size={15} color="var(--muted)" /> ברירות מחדל
        </button>
        <button type="button" className="btn ghost" onClick={() => setWizardOpen(true)} title="הטופס המלא — כל השדות, כולל הגדרות מתקדמות">
          הקמה מתקדמת
        </button>
        <button type="button" className="btn" onClick={() => setImportOpen(true)} disabled={all.length === 0}>
          <Icon name="upload" size={15} /> ייבוא לידים
        </button>
        <button type="button" className="btn primary" onClick={() => setSetupOpen(true)}>
          <Icon name="plus" size={16} /> קמפיין חדש
        </button>
      </div>

      {loading && all.length === 0 ? (
        <Card><div style={{ color: 'var(--muted)' }}>טוען…</div></Card>
      ) : all.length === 0 ? (
        <EmptyState icon="info" title="אין עדיין קמפיינים" description="צור קמפיין חיוג יוצא ראשון כדי להתחיל." action={<button type="button" className="btn primary" onClick={() => setSetupOpen(true)}>+ קמפיין חדש</button>} />
      ) : (
        // auto-fill (לא auto-fit) — עמודות ריקות נשארות, כך שכרטיס בודד לא נמתח לכל הרוחב
        list.length === 0 ? (
          <Card><div style={{ color: 'var(--muted)', textAlign: 'center' }}>אין קמפיינים בסינון הזה.</div></Card>
        ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(340px, 100%), 1fr))', gap: 16, alignItems: 'start' }}>
          {list.map((card, i) => (
            <CampaignCard
              key={card.id}
              index={i}
              card={card}
              joined={joinedIds.has(card.id)}
              dialing={dialingId === card.id}
              onJoinDial={() => void joinAndDial(card)}
              onLeave={() => void leaveCampaign(card)}
              onToggle={(next) => toggleActive(card, next)}
              onOpen={() => openCampaign(card)}
              onLeads={() => openCampaign(card, 'leads')}
              onAgents={() => openCampaign(card, 'agents')}
              onExport={() => exportLeads(card)}
              busy={resetBusyId === card.id}
              onReturnCapped={() => void returnCapped(card)}
              onRestart={() => setRestartTarget(card)}
            />
          ))}
        </div>
        )
      )}

      {importOpen && (
        <ImportLeadsModal
          campaignId={null}
          campaigns={all.map((c) => ({ id: c.id, name: c.name }))}
          onClose={() => setImportOpen(false)}
          onImported={() => { setImportOpen(false); void refetch(); }}
        />
      )}

      <Modal isOpen={setupOpen} onClose={() => setSetupOpen(false)} title="קמפיין חדש" size="lg">
        <CampaignSetupWizard
          onDone={() => { setSetupOpen(false); void refetch(); }}
          onCancel={() => setSetupOpen(false)}
        />
      </Modal>

      {/* הטופס הישן — נשאר זמנית כרשת ביטחון עד שהאשף החדש יוכיח את עצמו. */}
      <Modal isOpen={wizardOpen} onClose={() => setWizardOpen(false)} title="הקמה מתקדמת" size="lg">
        <Wizard steps={steps} onFinish={finish} onCancel={() => setWizardOpen(false)} />
      </Modal>

      <ConfirmDialog
        isOpen={!!restartTarget}
        onClose={() => setRestartTarget(null)}
        onConfirm={() => {
          const card = restartTarget;
          setRestartTarget(null);
          if (card) void restart(card);
        }}
        title="הפעלת הקמפיין מחדש"
        message={restartTarget
          ? `כל הלידים בקמפיין "${restartTarget.name}" יחזרו לתור ומונה הניסיונות יתאפס — כולל לידים שכבר טופלו ונסגרו. מסורבי פנייה (DNC) לא יושפעו.`
          : ''}
        confirmLabel="הפעל מחדש"
        tone="danger"
      />

      <ConfirmDialog
        isOpen={!!windowConfirm}
        onClose={() => setWindowConfirm(null)}
        onConfirm={() => {
          const card = windowConfirm?.card;
          setWindowConfirm(null);
          if (card) void joinAndDial(card, true);
        }}
        title="מחוץ לשעות החיוג"
        message={`${windowConfirm?.message ?? ''} לבצע חיוג בדיקה בכל זאת? הלקוחות בקמפיין יחויגו עכשיו.`}
        confirmLabel="חייג בכל זאת"
        tone="danger"
      />

      {defaults && (
        <DialerDefaultsModal
          isOpen={defaultsOpen}
          defaults={defaults}
          onClose={() => setDefaultsOpen(false)}
          onSaved={(next) => { setDefaults(next); setDefaultsOpen(false); }}
        />
      )}

      {/* פופ-אפ ניהול קמפיין — מסך גדול במקום מגירה צדדית */}
      <Modal
        isOpen={!!drawer}
        onClose={() => setDrawer(null)}
        size="xl"
        width="1000px"
        height="74vh"
        padded={false}
        title={drawer ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            {drawer.name}
            {/* תגית ה-ID: לחיצה מעתיקה — זה הערך של campaign_id בדחיפת לידים ב-API. */}
            <button
              type="button"
              onClick={() => { void navigator.clipboard?.writeText(String(drawer.id)); toast(`ID ${drawer.id} הועתק`, 'ok'); }}
              title="לחץ להעתקה — מזהה הקמפיין לשימוש ב-API (campaign_id)"
              style={{
                border: '1px solid var(--line)', background: 'var(--surface-2)', color: 'var(--text-2)',
                borderRadius: 'var(--r-pill)', padding: '1px 10px', fontSize: 'var(--t-xs)',
                fontWeight: 600, fontVariantNumeric: 'tabular-nums', cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              ID {drawer.id}
            </button>
            <StatusBadge tone={STATUS[drawer.status]?.tone ?? 'neutral'} dot size="sm">{STATUS[drawer.status]?.label ?? drawer.status}</StatusBadge>
            <span style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)', fontWeight: 400 }}>
              {TYPE_LABEL[drawer.type] ?? drawer.type} · {MODE_LABEL[drawer.dialer_mode] ?? drawer.dialer_mode}
            </span>
          </span>
        ) : ''}
        footer={drawer ? (
          <>
            <button type="button" className="btn danger" style={{ marginInlineEnd: 'auto' }} onClick={() => setDeleteTarget(drawer)}>מחק קמפיין</button>
            <button type="button" className="btn" onClick={() => void duplicate(drawer)} title="יוצר קמפיין חדש עם אותן הגדרות, נציגים ומספרים — בלי הלידים">שכפל</button>
            {drawer.status !== 'completed' && (
              <button type="button" className="btn" onClick={() => setStatus(drawer, 'completed')}>סיים קמפיין</button>
            )}
            {drawer.status === 'active' ? (
              <button type="button" className="btn" onClick={() => setStatus(drawer, 'paused')}>השהה</button>
            ) : (
              <button type="button" className="btn primary" onClick={() => setStatus(drawer, 'active')}>
                {drawer.status === 'paused' ? 'המשך' : 'הפעל'}
              </button>
            )}
          </>
        ) : undefined}
      >
        {drawer && (
          <>
            <div style={{ padding: '0 var(--pad-card)', flexShrink: 0 }}>
              <Tabs
                items={[
                  { id: 'overview', label: 'סקירה', icon: <Icon name="info" size={14} /> },
                  { id: 'mode', label: 'שיטת חיוג', icon: <Icon name="bolt" size={14} /> },
                  { id: 'settings', label: 'הגדרות מתקדמות', icon: <Icon name="settings" size={14} /> },
                  { id: 'agents', label: 'נציגים', icon: <Icon name="users" size={14} /> },
                  { id: 'leads', label: 'לידים', icon: <Icon name="target" size={14} /> },
                  { id: 'stats', label: 'סטטיסטיקה', icon: <Icon name="chart" size={14} /> },
                ]}
                value={tab}
                onChange={(id) => setTab(id as never)}
              />
            </div>
            <div style={{ padding: 'var(--pad-card)', overflow: 'auto', flex: 1 }}>
              {tab === 'overview' && (
                <div className="stack">
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
                    <InfoTile icon="target" label="סוג קמפיין" value={TYPE_LABEL[drawer.type] ?? drawer.type} />
                    <InfoTile icon="bolt" label="מצב חיוג" value={MODE_LABEL[drawer.dialer_mode] ?? drawer.dialer_mode} />
                    <InfoTile icon="flag" label="סטטוס" value={STATUS[drawer.status]?.label ?? drawer.status} />
                  </div>
                  {drawer.dialer_mode === 'predictive' && drawer.settings && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
                      {((drawer.settings.pacing_mode as string) ?? 'fixed') === 'fixed' ? (
                        <InfoTile icon="phone" label="קווים לכל נציג" value={String((drawer.settings.lines_per_agent as number) ?? 3)} />
                      ) : (
                        <InfoTile icon="phone" label="יחס חיוג מקסימלי" value={`×${(drawer.settings.dial_ratio_max as number) ?? 3}`} />
                      )}
                      <InfoTile icon="warn" label="רף נטישה" value={`${Math.round((((drawer.settings.abandon_rate_cap as number) ?? 0.03)) * 100)}%`} />
                      <InfoTile icon="voicemail" label="זיהוי משיבון (AMD)" value={(drawer.settings.amd_enabled as boolean) === false ? 'כבוי' : 'פעיל'} />
                    </div>
                  )}
                </div>
              )}
              {tab === 'mode' && (
                <DialModeTab
                  current={drawer.dialer_mode}
                  strategy={(drawer.settings?.dial_strategy as string | undefined) ?? undefined}
                  tenantDefaultStrategy={defaults?.dial_strategy}
                  onSelect={(mode) => changeMode(drawer, mode)}
                  onSelectStrategy={(s) => changeStrategy(drawer, s)}
                />
              )}
              {tab === 'settings' && (
                <CampaignSettingsTab
                  campaign={drawer}
                  defaults={defaults}
                  onSaved={(settings) => {
                    setDrawer((d) => (d && d.id === drawer.id ? { ...d, settings } : d));
                    void refetch();
                  }}
                />
              )}
              {tab === 'agents' && <CampaignAgentsTab campaignId={drawer.id} />}
              {tab === 'leads' && <CampaignLeadsTab campaignId={drawer.id} />}
              {tab === 'stats' && <CampaignStatsTab campaignId={drawer.id} />}
            </div>
          </>
        )}
      </Modal>

      {deleteTarget && (
        <Modal isOpen onClose={() => setDeleteTarget(null)} title="מחיקת קמפיין"
          footer={<><button type="button" className="btn" onClick={() => setDeleteTarget(null)}>ביטול</button><button type="button" className="btn danger" onClick={() => remove(deleteTarget)}>מחק</button></>}>
          <p>למחוק את הקמפיין <strong>{deleteTarget.name}</strong>? הלידים המשויכים יישארו אך לא ינותבו יותר.</p>
        </Modal>
      )}
    </div>
  );
}

function extractApiMessage(e: unknown, fallback: string): string {
  return (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback;
}

// לשונית "שיטת חיוג" — שלוש כרטיסיות צבעוניות, אחת לכל שיטה. לחיצה מחליפה את שיטת הקמפיין.
const MODE_META: { id: string; icon: Parameters<typeof Icon>[0]['name']; he: string; tagline: string; traits: string[]; bg: string; fg: string; soft: string }[] = [
  {
    id: 'preview',
    icon: 'eye',
    he: 'חיוג בהצגה מקדימה',
    tagline: 'הנציג רואה את פרטי הלקוח לפני השיחה ומחליט בעצמו מתי לחייג.',
    traits: ['מקסימום הכנה לפני כל שיחה', 'קצב איטי ומבוקר', 'מתאים לשיחות מורכבות ולידים יקרים'],
    bg: 'var(--info-soft)', fg: 'var(--info)', soft: 'var(--info-soft)',
  },
  {
    id: 'progressive',
    icon: 'bolt',
    he: 'חיוג אוטומטי רציף',
    tagline: 'המערכת מחייגת ללקוח הבא ברגע שנציג מתפנה — שיחה אחת לכל נציג.',
    traits: ['קצב אחיד בלי זמן מת', 'הלקוח תמיד עונה לנציג זמין', 'איזון טוב בין קצב לשירות'],
    bg: 'var(--ok-soft)', fg: 'var(--ok)', soft: 'var(--ok-soft)',
  },
  {
    id: 'predictive',
    icon: 'ai',
    he: 'חיוג חזוי',
    tagline: 'המערכת מחייגת לכמה לקוחות במקביל ומנבאת מתי יתפנה הנציג הבא.',
    traits: ['הקצב המהיר ביותר — רשימות גדולות', 'כמה קווים במקביל לכל נציג פנוי', 'שיחה עלולה להינטש אם אין נציג פנוי — רף הנטישה נשלט בהגדרות המתקדמות'],
    bg: 'var(--warn-soft)', fg: 'var(--warn)', soft: 'var(--warn-soft)',
  },
];

function DialModeTab({ current, strategy, tenantDefaultStrategy, onSelect, onSelectStrategy }: {
  current: string;
  strategy?: string;
  tenantDefaultStrategy?: string;
  onSelect: (mode: string) => void;
  onSelectStrategy: (strategy: string) => void;
}) {
  // הסדר בתוקף: override של הקמפיין, ואם אין — ברירת המחדל של העסק.
  const effectiveStrategy = strategy ?? tenantDefaultStrategy;
  return (
    <div className="stack" style={{ gap: 24 }}>
      <section className="stack" style={{ gap: 12 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 'var(--t-lg)', fontWeight: 700 }}>מצב החיוג — איך המערכת מחייגת</h3>
          <p style={{ margin: '4px 0 0', color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>
            לחיצה על כרטיסייה מחליפה את המצב מיידית.
          </p>
        </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 'var(--gap)', alignItems: 'stretch' }}>
        {MODE_META.map((m) => {
          const selected = m.id === current;
          return (
            <button
              key={m.id}
              type="button"
              className={`mode-card${selected ? ' selected' : ''}`}
              style={selected ? { borderColor: m.fg, background: m.soft } : undefined}
              onClick={() => !selected && onSelect(m.id)}
              aria-pressed={selected}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ display: 'inline-flex', padding: 10, background: selected ? m.fg : m.bg, color: selected ? 'var(--on-accent)' : m.fg, borderRadius: 'var(--r-3)' }}>
                  <Icon name={m.icon} size={22} />
                </span>
                {selected && (
                  <span style={{ fontSize: 'var(--t-xs)', fontWeight: 700, color: m.fg, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <Icon name="ok" size={14} /> בשימוש בקמפיין
                  </span>
                )}
              </div>
              <div>
                <div style={{ fontSize: 'var(--t-xl)', fontWeight: 700, lineHeight: 1.2 }}>{m.he}</div>
                <div dir="ltr" style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', fontWeight: 600, textAlign: 'end', letterSpacing: 0.4 }}>{MODE_LABEL[m.id]}</div>
              </div>
              <p style={{ margin: 0, fontSize: 'var(--t-sm)', color: 'var(--text-2)', lineHeight: 1.6 }}>{m.tagline}</p>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6, marginTop: 'auto' }}>
                {m.traits.map((t) => (
                  <li key={t} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 'var(--t-sm)', color: 'var(--text-2)' }}>
                    <span style={{ color: m.fg, flexShrink: 0, marginTop: 2 }}><Icon name="check" size={14} /></span>
                    {t}
                  </li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>
      </section>

      <section className="stack" style={{ gap: 12 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 'var(--t-lg)', fontWeight: 700 }}>סדר החיוג — איזה ליד מקבל את השיחה הבאה</h3>
          <p style={{ margin: '4px 0 0', color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>
            לחיצה קובעת את סדר החיוג לקמפיין הזה{tenantDefaultStrategy ? ' וגוברת על ברירת המחדל של העסק' : ''}.
          </p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 'var(--gap)', alignItems: 'stretch' }}>
          {DIAL_STRATEGIES.map((s) => {
            const hot = s.value.startsWith('hot');
            const fg = hot ? 'var(--warn)' : 'var(--info)';
            const soft = hot ? 'var(--warn-soft)' : 'var(--info-soft)';
            const selected = s.value === effectiveStrategy;
            const inheritedOnly = selected && !strategy;
            const title = s.label.replace(/\s*\(לא מומלץ\)\s*/, '');
            return (
              <button
                key={s.value}
                type="button"
                className={`mode-card${selected ? ' selected' : ''}`}
                style={selected ? { borderColor: fg, background: soft } : undefined}
                onClick={() => !selected && onSelectStrategy(s.value)}
                aria-pressed={selected}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ display: 'inline-flex', padding: 8, background: selected ? fg : soft, color: selected ? 'var(--on-accent)' : fg, borderRadius: 'var(--r-2)' }}>
                      <Icon name={hot ? 'bolt' : 'clock'} size={16} />
                    </span>
                    <span style={{ fontSize: 'var(--t-xs)', fontWeight: 700, color: fg }}>{hot ? 'לידים חמים' : 'לידים קרים'}</span>
                  </span>
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    {s.discouraged && <StatusBadge tone="warn" size="sm">לא מומלץ</StatusBadge>}
                    {selected && (
                      <span style={{ fontSize: 'var(--t-xs)', fontWeight: 700, color: fg, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <Icon name="ok" size={14} /> {inheritedOnly ? 'בתוקף (ברירת מחדל של העסק)' : 'בשימוש בקמפיין'}
                      </span>
                    )}
                  </span>
                </div>
                <div style={{ fontSize: 'var(--t-md)', fontWeight: 700, lineHeight: 1.3 }}>{title}</div>
                <p style={{ margin: 0, fontSize: 'var(--t-sm)', color: 'var(--text-2)', lineHeight: 1.6 }}>{s.description}</p>
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}

/** אריח מידע בפופ-אפ הקמפיין — אייקון, תווית וערך. */
function InfoTile({ icon, label, value }: { icon: Parameters<typeof Icon>[0]['name']; label: string; value: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, borderRadius: 'var(--r-3)', background: 'var(--surface-2)', border: '1px solid var(--line)' }}>
      <span style={{ display: 'inline-flex', padding: 8, background: 'var(--accent-soft)', color: 'var(--accent)', borderRadius: 'var(--r-2)', flexShrink: 0 }}>
        <Icon name={icon} size={16} />
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', fontWeight: 500 }}>{label}</div>
        <div style={{ fontSize: 'var(--t-md)', fontWeight: 700 }}>{value}</div>
      </div>
    </div>
  );
}

/** Tenant-wide dialer defaults editor (full DialerSettings). */
function DialerDefaultsModal({
  isOpen,
  defaults,
  onClose,
  onSaved,
}: {
  isOpen: boolean;
  defaults: DialerDefaults;
  onClose: () => void;
  onSaved: (next: DialerDefaults) => void;
}) {
  // Start from the current defaults; every field is populated (no inheritance).
  const [draft, setDraft] = useState<Partial<DialerSettings>>(() => stripStrategies(defaults));
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (isOpen) setDraft(stripStrategies(defaults)); }, [isOpen, defaults]);

  async function save() {
    setSaving(true);
    try {
      // Send only what actually changed vs the loaded defaults.
      const payload = buildSettingsPayload(draft, stripStrategies(defaults));
      const next = await adminApi.saveDialerDefaults(payload);
      toast('ברירת המחדל נשמרה — תחול על קמפיינים ללא הגדרה משלהם', 'ok');
      onSaved((next ?? { ...defaults, ...payload }) as DialerDefaults);
    } catch (e) {
      toast(extractApiMessage(e, 'שגיאה בשמירת ברירת המחדל'), 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="הגדרות ברירת מחדל לקמפיינים"
      size="lg"
      footer={<>
        <button type="button" className="btn" onClick={onClose} disabled={saving}>ביטול</button>
        <button type="button" className="btn primary" onClick={save} disabled={saving}>{saving ? 'שומר…' : 'שמור'}</button>
      </>}
    >
      <DialerSettingsForm value={draft} onChange={setDraft} />
    </Modal>
  );
}

function stripStrategies(d: DialerDefaults): Partial<DialerSettings> {
  const { strategies: _strategies, ...rest } = d;
  return rest;
}

/** Per-campaign advanced settings — overrides that fall back to the tenant default. */
function CampaignSettingsTab({
  campaign,
  defaults,
  onSaved,
}: {
  campaign: Campaign;
  defaults: DialerDefaults | null;
  onSaved: (settings: Record<string, unknown>) => void;
}) {
  const initial = (campaign.settings ?? {}) as Partial<DialerSettings>;
  const [draft, setDraft] = useState<Partial<DialerSettings>>(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft((campaign.settings ?? {}) as Partial<DialerSettings>); }, [campaign.id, campaign.settings]);

  const inherited = defaults ? stripStrategies(defaults) : undefined;

  async function save() {
    setSaving(true);
    try {
      // Merge with the campaign's existing settings so non-dialer keys survive.
      const settings = { ...(campaign.settings ?? {}), ...draft };
      await adminApi.updateCampaign(campaign.id, { settings });
      toast('הגדרות הקמפיין נשמרו', 'ok');
      onSaved(settings);
    } catch (e) {
      toast(extractApiMessage(e, 'שגיאה בשמירת ההגדרות'), 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="stack">
      <p style={{ margin: 0, fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>
        שדות שתשאיר ריקים יורשו מברירת המחדל של העסק. הזן ערך רק כדי לגבור עליה בקמפיין הזה.
      </p>
      <DialerSettingsForm value={draft} onChange={setDraft} inherited={inherited} />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button type="button" className="btn primary" onClick={save} disabled={saving}>{saving ? 'שומר…' : 'שמור הגדרות'}</button>
      </div>
      <Card title="בנק מספרים לחיוג (רוטציה)">
        <CampaignDidBank campaignId={campaign.id} />
      </Card>
      <Card title="סטטוסים (קודי סיום)">
        <CampaignDispositions campaignId={campaign.id} />
      </Card>
    </div>
  );
}

interface CAgent { id: number; name: string; role: string; extension?: string | null }

function CampaignAgentsTab({ campaignId }: { campaignId: number }) {
  const [agents, setAgents] = useState<CAgent[]>([]);
  const [available, setAvailable] = useState<CAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  function load() {
    setLoading(true);
    void adminApi
      .campaignAgents(campaignId)
      .then((d) => {
        setAgents((d.agents as CAgent[]) ?? []);
        setAvailable((d.available as CAgent[]) ?? []);
      })
      .catch(() => toast('שגיאה בטעינת נציגים', 'danger'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, [campaignId]);

  async function sync(ids: number[]) {
    setBusy(true);
    try {
      await adminApi.syncCampaignAgents(campaignId, ids);
      load();
    } catch {
      toast('שגיאה בעדכון', 'danger');
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div style={{ color: 'var(--muted)' }}>טוען…</div>;

  return (
    <div className="stack">
      <div>
        <h4 style={{ margin: '0 0 8px', fontSize: 'var(--t-sm)' }}>נציגי הקמפיין ({agents.length})</h4>
        {agents.length === 0 ? (
          <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>אין נציגים מוקצים.</div>
        ) : (
          <div style={{ display: 'grid', gap: 4 }}>
            {agents.map((a) => (
              <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 12px', background: 'var(--surface-2)', borderRadius: 'var(--r-2)' }}>
                <span>{a.name} <span style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>{labelFor('role', a.role)}</span></span>
                <button type="button" className="btn danger" disabled={busy} onClick={() => sync(agents.filter((x) => x.id !== a.id).map((x) => x.id))}>הסר</button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <h4 style={{ margin: '8px 0', fontSize: 'var(--t-sm)' }}>הוסף נציגים</h4>
        {available.length === 0 ? (
          <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>כל הנציגים כבר בקמפיין.</div>
        ) : (
          <div style={{ display: 'grid', gap: 4 }}>
            {available.map((a) => (
              <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 12px', border: '1px solid var(--line)', borderRadius: 'var(--r-2)' }}>
                <span>{a.name} <span style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>{labelFor('role', a.role)}</span></span>
                <button type="button" className="btn" disabled={busy} onClick={() => sync([...agents.map((x) => x.id), a.id])}>הוסף</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const LEAD_STATUS: Record<string, string> = {
  queued: 'בתור', dialing: 'מחייג', contacted: 'נוצר קשר', completed: 'הושלם',
  failed: 'נכשל', dnc: 'אל תחייג', scheduled: 'מתוזמן',
};

/** מיוצא לבדיקות: לשונית הלידים של הקמפיין (בחירה, שיוך והסרה). */
export function CampaignLeadsTab({ campaignId }: { campaignId: number }) {
  const [leads, setLeads] = useState<CampaignLead[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [assigned, setAssigned] = useState(''); // '' | 'none' | '<agentId>'
  const [ownership, setOwnership] = useState<LeadOwnership>('shared');
  const [savingOwnership, setSavingOwnership] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [autoOpen, setAutoOpen] = useState(false);
  const [toAgentOpen, setToAgentOpen] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  // "בחר את כל N הלידים" — חורג מהעמוד הנטען; הפעולה תרוץ בשרת על הסינון.
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [campaignAgents, setCampaignAgents] = useState<CAgent[]>([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    void adminApi
      .campaignLeads(campaignId, { status: status || undefined, assigned: assigned || undefined, per_page: 100 })
      .then((res) => {
        setLeads(res.data ?? []);
        setTotal(res.meta?.total ?? (res.data ?? []).length);
        if (res.lead_ownership) setOwnership(res.lead_ownership);
        setSelected(new Set());
        setSelectAllMatching(false);
      })
      .catch(() => toast('שגיאה בטעינת לידים', 'danger'))
      .finally(() => setLoading(false));
  }, [campaignId, status, assigned]);

  useEffect(() => { load(); }, [load]);

  // נציגי הקמפיין — לבוררי השיוך.
  useEffect(() => {
    void adminApi.campaignAgents(campaignId).then((d) => setCampaignAgents((d.agents as CAgent[]) ?? [])).catch(() => {});
  }, [campaignId]);

  async function changeOwnership(next: LeadOwnership) {
    setOwnership(next); // אופטימי
    setSavingOwnership(true);
    try {
      await adminApi.updateCampaign(campaignId, { settings: { lead_ownership: next } });
      toast('שיטת חלוקת הלידים נשמרה', 'ok');
      load();
    } catch {
      setOwnership(next === 'shared' ? 'assigned' : 'shared');
      toast('שגיאה בשמירה', 'danger');
    } finally {
      setSavingOwnership(false);
    }
  }

  async function runAssign(body: AssignLeadsBody) {
    setBusy(true);
    try {
      const res = await adminApi.assignCampaignLeads(campaignId, body);
      toast(`עודכנו ${res.assigned} לידים`, 'ok');
      setAutoOpen(false); setToAgentOpen(false);
      load();
    } catch (e) {
      toast(extractApiMessage(e, 'שגיאה בשיוך'), 'danger');
    } finally {
      setBusy(false);
    }
  }

  function toggleSel(id: number) {
    setSelected((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
    setSelectAllMatching(false);
  }

  const selIds = [...selected];
  const allOnPageSelected = leads.length > 0 && selIds.length === leads.length;
  // כמה לידים באמת ייגעו: "כל התואמים לסינון" או רק מה שסומן בעמוד.
  const affected = selectAllMatching ? total : selIds.length;

  /** בחירת/ביטול כל הלידים בעמוד הנוכחי. */
  function toggleSelectPage(on: boolean) {
    setSelected(on ? new Set(leads.map((l) => l.id)) : new Set());
    if (!on) setSelectAllMatching(false);
  }

  async function removeLeads() {
    setBusy(true);
    try {
      const res = selectAllMatching
        ? await adminApi.deleteCampaignLeads(campaignId, { all: true, status: status || undefined, assigned: assigned || undefined })
        : await adminApi.deleteCampaignLeads(campaignId, { lead_ids: selIds });
      toast(
        res.skipped_calling > 0
          ? `הוסרו ${res.deleted} לידים · ${res.skipped_calling} בחיוג פעיל לא הוסרו`
          : `הוסרו ${res.deleted} לידים מהקמפיין`,
        'ok',
      );
      setConfirmDelete(false);
      setSelectAllMatching(false);
      load();
    } catch (e) {
      toast(extractApiMessage(e, 'שגיאה בהסרת לידים'), 'danger');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      {/* חלוקת לידים — מאגר משותף / מחולק לנציגים */}
      <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', padding: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <strong style={{ fontSize: 'var(--t-sm)' }}>חלוקת לידים</strong>
          <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 2 }}>
            {ownership === 'assigned' ? 'כל נציג מחייג רק ללידים המשויכים אליו (ולא-משויכים).' : 'כל הנציגים מושכים מאותו מאגר משותף.'}
          </div>
        </div>
        <div style={{ display: 'inline-flex', gap: 4 }}>
          <button type="button" className={`btn ${ownership === 'shared' ? 'primary' : 'ghost'}`} style={{ padding: '4px 10px', fontSize: 'var(--t-xs)' }} disabled={savingOwnership} onClick={() => ownership !== 'shared' && changeOwnership('shared')}>מאגר משותף</button>
          <button type="button" className={`btn ${ownership === 'assigned' ? 'primary' : 'ghost'}`} style={{ padding: '4px 10px', fontSize: 'var(--t-xs)' }} disabled={savingOwnership} onClick={() => ownership !== 'assigned' && changeOwnership('assigned')}>מחולק לנציגים</button>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <strong>לידים ({total})</strong>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ padding: '6px 8px', border: '1px solid var(--line)', borderRadius: 'var(--r-2)', background: 'var(--surface)' }}>
            <option value="">כל הסטטוסים</option>
            {Object.entries(LEAD_STATUS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={assigned} onChange={(e) => setAssigned(e.target.value)} style={{ padding: '6px 8px', border: '1px solid var(--line)', borderRadius: 'var(--r-2)', background: 'var(--surface)' }}>
            <option value="">כל השיוכים</option>
            <option value="none">לא משויך</option>
            {campaignAgents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <button type="button" className="btn" onClick={() => setAutoOpen(true)}>חלק אוטומטית</button>
          <button type="button" className="btn primary" onClick={() => setImportOpen(true)}>ייבוא לידים</button>
        </div>
      </div>

      {/* שורת בחירה: בחר הכל בעמוד, ומשם גם "כל N התואמים לסינון". */}
      {leads.length > 0 && (
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 'var(--t-sm)', color: 'var(--text-2)', padding: '0 4px' }}>
          <input
            type="checkbox"
            checked={allOnPageSelected}
            ref={(el) => { if (el) el.indeterminate = selIds.length > 0 && !allOnPageSelected; }}
            onChange={(e) => toggleSelectPage(e.target.checked)}
            aria-label="בחר את כל הלידים בעמוד"
          />
          בחר הכל{leads.length < total ? ` (${leads.length} בעמוד)` : ''}
        </label>
      )}

      {/* פעולות על נבחרים */}
      {selIds.length > 0 && (
        <div className="stack" style={{ gap: 6, padding: '8px 10px', background: 'var(--accent-soft, var(--surface-2))', borderRadius: 'var(--r-2)' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 'var(--t-sm)', fontWeight: 600 }}>
              {selectAllMatching ? `כל ${total} הלידים נבחרו` : `${selIds.length} נבחרו`}
            </span>
            <button type="button" className="btn" style={{ height: 30 }} disabled={busy} onClick={() => setToAgentOpen(true)}>שייך לנציג…</button>
            <button type="button" className="btn" style={{ height: 30 }} disabled={busy} onClick={() => runAssign({ mode: 'clear', lead_ids: selIds })}>החזר למאגר</button>
            <span style={{ flex: 1 }} />
            <button type="button" className="btn danger" style={{ height: 30 }} disabled={busy} onClick={() => setConfirmDelete(true)}>
              <Icon name="trash" size={13} /> הסר מהקמפיין
            </button>
          </div>
          {/* חריגה מעבר לעמוד — רק כשיש מה לחרוג אליו. */}
          {allOnPageSelected && total > leads.length && (
            <div style={{ fontSize: 'var(--t-xs)', color: 'var(--text-2)' }}>
              {selectAllMatching ? (
                <>נבחרו כל {total} הלידים התואמים לסינון. <button type="button" className="btn ghost" style={{ height: 22, padding: '0 8px', fontSize: 'var(--t-xs)' }} onClick={() => setSelectAllMatching(false)}>בטל</button></>
              ) : (
                <>נבחרו {leads.length} לידים בעמוד הזה. <button type="button" className="btn ghost" style={{ height: 22, padding: '0 8px', fontSize: 'var(--t-xs)' }} onClick={() => setSelectAllMatching(true)}>בחר את כל {total} הלידים</button></>
              )}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--muted)' }}>טוען…</div>
      ) : leads.length === 0 ? (
        <div style={{ padding: 'var(--pad-card)', background: 'var(--surface-2)', borderRadius: 'var(--r-2)', color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>
          אין לידים{status ? ' בסטטוס זה' : ''}. ייבא רשימה כדי להתחיל לחייג.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 4 }}>
          {leads.map((l) => (
            <div key={l.id} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto auto auto', gap: 8, alignItems: 'center', padding: '6px 12px', background: 'var(--surface-2)', borderRadius: 'var(--r-2)' }}>
              <input type="checkbox" checked={selected.has(l.id)} onChange={() => toggleSel(l.id)} aria-label="בחר ליד" />
              <div>
                <div style={{ fontWeight: 600 }}>{l.name || <span dir="ltr">{l.phone}</span>}</div>
                {l.name && <div dir="ltr" style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{l.phone}</div>}
              </div>
              <span style={{ fontSize: 'var(--t-xs)', color: l.assigned_agent_name ? 'var(--accent-ink)' : 'var(--muted)' }}>
                {l.assigned_agent_name ?? 'לא משויך'}
              </span>
              <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{l.attempts} ניסיונות</span>
              <StatusBadge tone={l.status === 'completed' ? 'ok' : l.status === 'failed' || l.status === 'dnc' ? 'danger' : 'neutral'} size="sm" dot>
                {LEAD_STATUS[l.status] ?? l.status}
              </StatusBadge>
            </div>
          ))}
        </div>
      )}

      {importOpen && (
        <ImportLeadsModal
          campaignId={campaignId}
          onClose={() => setImportOpen(false)}
          onImported={() => { setImportOpen(false); load(); }}
        />
      )}

      {autoOpen && (
        <AutoAssignModal
          agents={campaignAgents}
          busy={busy}
          onClose={() => setAutoOpen(false)}
          onSubmit={(agentIds, reassign) => runAssign({ mode: 'round_robin', agent_ids: agentIds, reassign })}
        />
      )}

      {confirmDelete && (
        <Modal
          isOpen
          onClose={() => setConfirmDelete(false)}
          title="הסרת לידים מהקמפיין"
          footer={
            <>
              <button type="button" className="btn" onClick={() => setConfirmDelete(false)}>ביטול</button>
              <button type="button" className="btn danger" disabled={busy} onClick={() => void removeLeads()}>
                {busy ? 'מסיר…' : `הסר ${affected} לידים`}
              </button>
            </>
          }
        >
          <p style={{ margin: 0 }}>
            להסיר <strong>{affected}</strong> לידים מהקמפיין? הלידים יימחקו מהקמפיין הזה ולא יחויגו יותר.
          </p>
          <p style={{ margin: '8px 0 0', fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>
            ליד שנמצא כרגע בחיוג פעיל לא יוסר. אנשי הקשר עצמם נשארים במערכת.
          </p>
        </Modal>
      )}

      {toAgentOpen && (
        <ToAgentModal
          agents={campaignAgents}
          count={selIds.length}
          busy={busy}
          onClose={() => setToAgentOpen(false)}
          onSubmit={(agentId) => runAssign({ mode: 'to_agent', agent_id: agentId, lead_ids: selIds })}
        />
      )}
    </div>
  );
}

// דיאלוג "חלק אוטומטית" — בחירת נציגים + חלוקה-מחדש אופציונלית (round-robin).
function AutoAssignModal({ agents, busy, onClose, onSubmit }: {
  agents: CAgent[];
  busy: boolean;
  onClose: () => void;
  onSubmit: (agentIds: number[], reassign: boolean) => void;
}) {
  const [picked, setPicked] = useState<number[]>(agents.map((a) => a.id));
  const [reassign, setReassign] = useState(false);
  function toggle(id: number) { setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id])); }
  return (
    <Modal isOpen onClose={onClose} title="חלוקת לידים אוטומטית"
      footer={<>
        <button type="button" className="btn" onClick={onClose} disabled={busy}>ביטול</button>
        <button type="button" className="btn primary" onClick={() => onSubmit(picked, reassign)} disabled={busy || picked.length === 0}>{busy ? 'מחלק…' : 'חלק'}</button>
      </>}>
      <div className="stack">
        <p style={{ margin: 0, fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>הלידים יחולקו שווה בשווה (round-robin) בין הנציגים שנבחרו.</p>
        {agents.length === 0 ? (
          <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>אין נציגים בקמפיין. הוסף נציגים בלשונית "נציגים".</div>
        ) : (
          <div style={{ display: 'grid', gap: 4 }}>
            {agents.map((a) => (
              <label key={a.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 'var(--t-sm)', cursor: 'pointer' }}>
                <input type="checkbox" checked={picked.includes(a.id)} onChange={() => toggle(a.id)} />
                {a.name}
              </label>
            ))}
          </div>
        )}
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 'var(--t-sm)', cursor: 'pointer', borderTop: '1px solid var(--line)', paddingTop: 8 }}>
          <input type="checkbox" checked={reassign} onChange={(e) => setReassign(e.target.checked)} />
          חלק מחדש גם לידים שכבר משויכים
        </label>
      </div>
    </Modal>
  );
}

// דיאלוג "שייך לנציג" — לידים נבחרים לנציג יחיד.
function ToAgentModal({ agents, count, busy, onClose, onSubmit }: {
  agents: CAgent[];
  count: number;
  busy: boolean;
  onClose: () => void;
  onSubmit: (agentId: number) => void;
}) {
  const [agentId, setAgentId] = useState('');
  return (
    <Modal isOpen onClose={onClose} title={`שיוך ${count} לידים לנציג`}
      footer={<>
        <button type="button" className="btn" onClick={onClose} disabled={busy}>ביטול</button>
        <button type="button" className="btn primary" onClick={() => agentId && onSubmit(Number(agentId))} disabled={busy || !agentId}>{busy ? 'משייך…' : 'שייך'}</button>
      </>}>
      <FormField label="נציג" required>
        <select value={agentId} onChange={(e) => setAgentId(e.target.value)} style={{ width: '100%', padding: 10, border: '1px solid var(--line)', borderRadius: 'var(--r-2)', background: 'var(--surface)' }}>
          <option value="">— בחר נציג —</option>
          {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </FormField>
    </Modal>
  );
}

function ImportLeadsModal({ campaignId, campaigns, onClose, onImported }: {
  /** null = נבחר בחלון (ייבוא מהסרגל העליון של מסך הקמפיינים). */
  campaignId: number | null;
  campaigns?: { id: number; name: string }[];
  onClose: () => void;
  onImported: () => void;
}) {
  const [parsed, setParsed] = useState<ParsedLead[]>([]);
  const [saving, setSaving] = useState(false);
  const [pickedId, setPickedId] = useState('');
  const targetId = campaignId ?? (pickedId ? Number(pickedId) : null);

  async function save() {
    if (targetId == null) { toast('בחר קמפיין יעד לייבוא', 'danger'); return; }
    if (parsed.length === 0) { toast('לא נמצאו מספרים תקינים', 'danger'); return; }
    setSaving(true);
    try {
      const res = await adminApi.importLeads(targetId, parsed);
      toast(`יובאו ${res.imported} לידים${res.skipped_duplicates ? ` · ${res.skipped_duplicates} כפילויות דולגו` : ''}`, 'ok');
      onImported();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה בייבוא';
      toast(msg, 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal isOpen onClose={onClose} title="ייבוא לידים"
      footer={<><button type="button" className="btn" onClick={onClose} disabled={saving}>ביטול</button><button type="button" className="btn primary" onClick={save} disabled={saving}>{saving ? 'מייבא…' : `ייבא ${parsed.length || ''}`}</button></>}>
      {campaignId == null && (
        <FormField label="קמפיין יעד" required>
          <select value={pickedId} onChange={(e) => setPickedId(e.target.value)} aria-label="קמפיין יעד"
            style={{ width: '100%', padding: 10, border: '1px solid var(--line)', borderRadius: 'var(--r-2)', background: 'var(--surface)', color: 'var(--text)' }}>
            <option value="">— בחר קמפיין —</option>
            {(campaigns ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </FormField>
      )}
      <LeadImportInput onParsed={setParsed} />
    </Modal>
  );
}

interface CStats {
  total_leads: number;
  contacted: number;
  completed: number;
  deals: number;
  conversion_rate: number;
  by_status: Record<string, number>;
}

function CampaignStatsTab({ campaignId }: { campaignId: number }) {
  const [stats, setStats] = useState<CStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    void adminApi
      .campaignStats(campaignId)
      .then((d) => setStats(d as CStats))
      .catch(() => toast('שגיאה בטעינת סטטיסטיקה', 'danger'))
      .finally(() => setLoading(false));
  }, [campaignId]);

  if (loading) return <div style={{ color: 'var(--muted)' }}>טוען…</div>;
  if (!stats) return <div style={{ color: 'var(--muted)' }}>אין נתונים.</div>;

  const cards = [
    { label: 'סה״כ לידים', value: stats.total_leads },
    { label: 'יצרו קשר', value: stats.contacted },
    { label: 'הושלמו', value: stats.completed },
    { label: 'עסקאות', value: stats.deals },
  ];

  return (
    <div className="stack">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8 }}>
        {cards.map((s) => (
          <div key={s.label} style={{ padding: 'var(--pad-card)', background: 'var(--surface-2)', borderRadius: 'var(--r-2)' }}>
            <div style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>{s.label}</div>
            <div style={{ fontSize: 'var(--t-2xl)', fontWeight: 700, marginTop: 4 }}>{s.value}</div>
          </div>
        ))}
      </div>
      <div style={{ padding: 'var(--pad-card)', background: 'var(--surface-2)', borderRadius: 'var(--r-2)', textAlign: 'center' }}>
        <div style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>יחס המרה</div>
        <div style={{ fontSize: 'var(--t-2xl)', fontWeight: 700, color: 'var(--accent)' }}>{stats.conversion_rate}%</div>
      </div>
      {Object.keys(stats.by_status).length > 0 && (
        <div>
          <h4 style={{ margin: '8px 0', fontSize: 'var(--t-sm)' }}>לפי סטטוס ליד</h4>
          <div style={{ display: 'grid', gap: 4 }}>
            {Object.entries(stats.by_status).map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', background: 'var(--surface-2)', borderRadius: 'var(--r-2)' }}>
                <span>{k}</span><strong>{v}</strong>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
